// Tab registry + URL<->tab sync extracted from ConsentPage (Constitution III
// D19 entry-gate decomposition, T042 — ConsentPage.js sat at 397/400 with no
// headroom for the Wave-1 Requirements + Evidence tabs). Kept as its own hook
// so future tabs are added HERE, not back onto the page orchestrator.
import { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, ClipboardList, FileText, Globe, LayoutDashboard, Upload } from 'lucide-react';

// Tab registry: id, i18n label key, icon. ConsentPage filters this by
// permission (canImport/canCurate) before rendering the switcher. Exported
// (053 residual fix, H-221) so measurementModeUiAbsence.test.js's exact-set
// pin can import it directly — the hook's `tabs` return is still the one
// path ConsentPage itself uses; this named export exists for that
// verification surface, not as a second way to consume the registry at
// runtime.
//
// 053 T098 (US1) — 'requirements' is a REAL Wave-1 Phase-1 deliverable (the
// purpose-grain requirement register), NOT the deferred measurement-mode/
// phase-2 surface measurementModeUiAbsence.test.js's docstring warns
// against (that contract is about "coverage"/"cohorts"/"population
// binding" gated on capabilities.measurementMode — a different, not-yet-
// real object). Adding this tab is the "conscious update" that test's own
// comments anticipate; its EXPECTED_TAB_IDS/EXPECTED_CONSENT_ROUTES pins
// are updated in the same PR.
// 053 G3 (owner ruling 2026-08-19, applied to purpose curation 2026-08-23) —
// the 'curation' tab is REMOVED, and with it its ROUTE_BY_TAB entry, so the
// registry<->table totality below stays TRUE rather than merely unpinned. It
// was never a second purpose inventory (its label `consent.curation.nav` was
// byte-identical to the ROPA sidebar's `nav.purposeCuration`, which is exactly
// why it read as one) — it is a resolution queue for consent RECORDS that
// arrived with only a free-text purpose. That work now has two homes: a
// DPOTask in the single DPO inbox (`consent_curation:purpose_unresolved`,
// service.py) and `CurationQueuePanel` on the Records surface, both deep-
// linking to `/consent/purpose-curation` — a route that REMAINS mounted in
// App.js behind its `view_compliance` guard, now rendering the standalone
// `ConsentPurposeCurationPage`. Do NOT restore this entry, and do NOT delete
// that route: REQUIREMENT-kind rows with a NULL `processing_purpose_id` appear
// on no other surface (the register is purpose-grain; the Records list
// defaults to CONSENT-kind), so the page is the only place they are reachable.
export const CONSENT_TABS = [
  { id: 'records',       labelKey: 'consentRecords',            icon: CheckCircle },
  { id: 'websites',      labelKey: 'websites',                  icon: Globe },
  { id: 'requirements',  labelKey: 'consent.register.nav',      icon: ClipboardList },
  // 053 T139 (US3) — the Evidence tab: attestations of HOW consent is
  // collected, per requirement-register row. Same "conscious update" as
  // 'requirements' above, and the same pins move with it in this PR.
  // FileText, deliberately NOT a check-shaped icon: an attestation is a
  // human claim, and nothing on this surface may echo the CheckCircle
  // success idiom (DNA §3.21a / SME condition 1).
  { id: 'evidence',      labelKey: 'consent.attestation.nav',   icon: FileText },
  { id: 'commandCenter', labelKey: 'consent.commandCenter.nav', icon: LayoutDashboard },
  { id: 'import',        labelKey: 'consent.import.nav',        icon: Upload },
];

// Tab -> route. TOTAL over CONSENT_TABS: every tab owns an address, and the
// pair (registry, table) is pinned to cover the same set by
// consentTabRouting.test.js.
//
// ⚖️ Totality is the whole fix, not tidiness. This table used to omit BOTH
// 'records' (documented as "the root route, and the fallback") and 'websites'
// (documented as nothing at all). 'records' survived the omission because the
// `?? '/consent'` fallback happened to name its route. 'websites' did not:
// clicking Cookie Consent navigated to '/consent', the pathname reading of
// '/consent' is 'records', and the sync effect that no longer exists then
// overwrote the click. Seven tabs, six addresses — the owner's "I click on a
// tab and find myself in another tab", in one missing key.
//
// Every route named here except the root is wrapped in its own
// `<GuardedRoute permission="...">` in App.js (curation/import notably gate on
// view_compliance/manage_compliance, wider than the bare /consent route's
// view_consent) — see `ConsentTabQueryRedirect` below for why that matters.
// '/consent/websites' is gated view_consent, exactly the gate the tab already
// rendered under when it lived at the bare root: giving it an address must not
// move it to a different permission.
export const ROUTE_BY_TAB = {
  records: '/consent',
  websites: '/consent/websites',
  requirements: '/consent/requirements',
  evidence: '/consent/evidence',
  commandCenter: '/consent/command-center',
  import: '/consent/import',
};

// RETIRED tab ids that still have to resolve as `?tab=` deep links.
//
// ⚖️ Kept SEPARATE from ROUTE_BY_TAB on purpose. That table is the tab
// registry's twin and must stay total over CONSENT_TABS — putting 'curation'
// back into it would re-add a tab. But an address is not a tab: a bookmark or
// a pasted link written before the G3 relocation still says `?tab=curation`,
// and resolving it to DEFAULT_ROUTE would render Consent Records while the
// operator asked for the resolution queue. That is exactly the "I click on a
// tab and find myself in another tab" symptom this file exists to kill, in its
// deep-link form — silent, and worse for being silent.
//
// Consulted only by ConsentTabQueryRedirect, only after ROUTE_BY_TAB misses.
// A retired id belongs here for as long as links to it can exist in the wild.
const REDIRECT_ALIAS_BY_TAB = {
  curation: '/consent/purpose-curation',
};

// The tab a bare/unrecognized consent address resolves to.
const DEFAULT_TAB = 'records';
const DEFAULT_ROUTE = ROUTE_BY_TAB[DEFAULT_TAB];

// Route -> tab, INVERTED from the table above rather than written out a second
// time: two hand-maintained tables of one fact is the same defect class as two
// runtime representations of one fact, one build earlier. Returns null for any
// pathname that is not a tab address (e.g. /consent/identity-queue, or a deep
// sub-route), so the caller applies the default explicitly.
const TAB_BY_ROUTE = Object.fromEntries(
  Object.entries(ROUTE_BY_TAB).map(([tab, route]) => [route, tab]),
);

const tabForPathname = (pathname) => TAB_BY_ROUTE[pathname] ?? null;

/**
 * Owns the consent module's tab registry and reads the active tab off the URL.
 *
 * ⚖️ THE URL IS THE SINGLE SOURCE OF TRUTH for which tab is active. `activeTab`
 * is DERIVED from `location.pathname` on every render — there is no `useState`
 * mirroring it and no effect syncing the two, because there is no second copy
 * to sync. That is deliberate: the previous version held the tab in state AND
 * in the URL, and every entry path that changed one without the other (a click
 * on a tab with no address, a reload, back/forward, a `?tab=` deep link) let
 * the two disagree — visibly, for one frame, before the URL won.
 *
 * The URL wins the "which representation" argument rather than state because
 * it is the only one that survives a reload, is addressable by a DPOTask
 * `action_url`, is restored by the back button, and is permission-checked by a
 * `<GuardedRoute>`. State can do none of those. The cost — every tab needs a
 * route — is paid in full by ROUTE_BY_TAB above.
 */
export default function useConsentTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = tabForPathname(location.pathname) ?? DEFAULT_TAB;

  // A tab change is a change of address and nothing else — no local state to
  // set, so nothing can be set and then contradicted. `replace` keeps the tab
  // switcher a view toggle rather than a journey: clicking through seven tabs
  // must not bury the page the operator arrived from under seven entries they
  // have to press Back through to escape.
  const handleTabChange = useCallback((tab) => {
    navigate(ROUTE_BY_TAB[tab] ?? DEFAULT_ROUTE, { replace: true });
  }, [navigate]);

  return { activeTab, handleTabChange, tabs: CONSENT_TABS };
}

/**
 * 053 tail-lane fix — `?tab=<id>` on the bare `/consent` path. Every DPOTask
 * `action_url` of the form `/consent?tab=records` (guardian-capacity,
 * explicit-escalation) only ever landed on the Records tab because 'records'
 * is ALSO the fallback `tabForPathname` returns for a bare `/consent`
 * pathname — the query string itself was never read. That is harmless while
 * every writer happens to name the fallback tab, and silently mis-lands the
 * day one names any other.
 *
 * Wrap the `/consent` route's element in THIS component (App.js), OUTSIDE
 * `<ConsentPage>`, rather than reading `?tab=` inside `useConsentTabs` with a
 * second `navigate()` effect: `ConsentPage` also mounts `useURLFilters`,
 * whose own `setSearchParams` effect fires in the SAME commit and resolves
 * its relative `"?" + params` navigation against the router's synchronous
 * state — which still reads the PRE-navigation pathname at that instant,
 * because a `navigate()` call does not retroactively update a sibling
 * hook's effect running later in the same flush. The result, verified live:
 * the tab-redirecting `navigate('/consent/requirements')` fires, then
 * `useURLFilters`'s own normalising `setFilter` effect fires straight after
 * and silently overwrites it back to `/consent`. Redirecting BEFORE
 * `ConsentPage` (and `useURLFilters`) ever mounts removes the race instead
 * of trying to win it — nothing here competes with anything, because nothing
 * else is mounted yet.
 *
 * EVERY tab id redirects, with no exceptions carved out. There used to be two:
 * 'records' and 'websites' were listed as LOCAL_ONLY_TABS and skipped, on the
 * stated grounds that a `?tab=` naming them "is honoured directly as local tab
 * state". No code ever did that — nothing downstream read the query string at
 * all — so `?tab=websites` rendered Consent Records while the address still
 * said `tab=websites`: the URL right and the panel wrong, which is the
 * reported symptom in its purest form. Now that every tab owns an address the
 * exception has nothing left to describe, and the honest rule is the simple
 * one: a `?tab=` naming any tab goes to that tab's route.
 *
 * Each destination is its OWN `<GuardedRoute>`-wrapped path — `?tab=import`
 * handed to a viewer without `manage_compliance` must still be refused by
 * `/consent/import`'s guard, never silently rendered under the bare
 * `view_consent`-gated `/consent` path (`ConsentList.js` renders
 * `<ConsentImport>` behind `activeTab` alone, with no permission check of its
 * own). An UNRECOGNIZED `?tab=` value resolves to the default tab's route,
 * which strips the param rather than leaving an address that names a tab the
 * module does not have.
 */
export function ConsentTabQueryRedirect({ children }) {
  const location = useLocation();
  // Self-guarded on the pathname too, not only on WHERE App.js happens to
  // mount this — a `?tab=` stray on an already-routed path (e.g. carried
  // over from a copy-pasted link) must never fight that path, which is the
  // router's own permission-checked source of truth.
  const params = location.pathname === DEFAULT_ROUTE
    ? new URLSearchParams(location.search)
    : null;
  const tab = params ? params.get('tab') : null;
  const route = tab ? (ROUTE_BY_TAB[tab] ?? REDIRECT_ALIAS_BY_TAB[tab] ?? DEFAULT_ROUTE) : undefined;
  if (route) {
    // Carry every OTHER param through. `tab` is the only one this component
    // consumes; `ConsentPage` mounts `useURLFilters(filterSchema)` and reads
    // `search` / `status` / `type` off the query string on the destination
    // route just as it does here, so redirecting to a bare pathname would
    // silently drop a filtered deep link's filters — `/consent?tab=
    // requirements&status=active` would arrive unfiltered, showing MORE rows
    // than the link promised. Dropping `tab` itself keeps the destination URL
    // honest — the tab is carried by the pathname there — and is now also what
    // TERMINATES this redirect: `?tab=records` (and any unrecognized value)
    // resolves to DEFAULT_ROUTE, i.e. the pathname we are already on, so the
    // pathname guard above cannot be what stops a second pass. Removing the
    // param can, and does: the next render reads no `tab` and renders children.
    params.delete('tab');
    const query = params.toString();
    return <Navigate to={query ? `${route}?${query}` : route} replace />;
  }
  return children;
}
